import React, { useEffect, useRef, useState } from 'react';

// Shared by the Captures page (CaptureHandler.js) and the dashboard
// (Projects.js).
//
// <img> that keeps retrying a failed load: 1, 2, 4, 8, 16 s apart (~30 s in
// all), then shows a "tap to retry" placeholder, and tries again by itself
// when the tab becomes visible or the network comes back. The old retry
// gave up after ~3 s, which a short CDN/network hiccup easily outlasted.
export const IMG_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

export default function RetryImage({ src, alt, className, style, onTransitionEnd, quietFail = false, ...rest }) {
  const [nonce, setNonce] = useState(0);
  const [tries, setTries] = useState(0);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setNonce(0);
    setTries(0);
    setFailed(false);
    return () => clearTimeout(timerRef.current);
  }, [src]);

  const retryNow = () => {
    clearTimeout(timerRef.current);
    setFailed(false);
    setTries(0);
    setNonce((n) => n + 1);
  };

  useEffect(() => {
    if (!failed) return;
    const again = () => {
      if (document.visibilityState === 'visible' && navigator.onLine !== false) retryNow();
    };
    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('online', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [failed]);

  const handleError = () => {
    if (tries >= IMG_RETRY_DELAYS_MS.length) {
      console.warn('Image load failed after retries:', src);
      setFailed(true);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setTries((t) => t + 1);
      setNonce((n) => n + 1);
    }, IMG_RETRY_DELAYS_MS[tries]);
  };

  if (failed) {
    // quietFail: show nothing (e.g. a dashboard card, where tapping must
    // open the splat, not retry) — still retries on visibility / online.
    if (quietFail) return null;
    return (
      <div
        className={`${className} capture-load-failed`}
        style={style}
        onTransitionEnd={onTransitionEnd}
        role="button"
        onClick={(e) => { e.stopPropagation(); retryNow(); }}
        onDoubleClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); retryNow(); }}
      >
        Couldn't load image. Tap to retry
      </div>
    );
  }

  const url = nonce ? `${src}${src.includes('?') ? '&' : '?'}r=${nonce}` : src;
  return <img src={url} alt={alt} className={className} style={style} onTransitionEnd={onTransitionEnd} onError={handleError} {...rest} />;
}
